import React from 'react';
import styled, { css } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { borders } from '../tokens/borders';
import { shadows } from '../tokens/shadows';
import { animations } from '../tokens/animations';
import { spacing } from '../tokens/spacing';
import { getPatternStyles, PatternType, PatternIntensity } from '../../utils/backgroundPatterns';
import { generateGradient } from '../../utils/colorUtils';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'filled' | 'glass';
export type CardHeaderVariant = 'default' | 'gradient' | 'accent';

interface CardProps {
  id?: string;
  variant?: CardVariant;
  headerVariant?: CardHeaderVariant;
  pattern?: PatternType;
  patternIntensity?: PatternIntensity;
  accentBorder?: 'top' | 'bottom' | 'left' | 'right' | boolean;
  hoverElevation?: boolean;
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  actions?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  /** Accessibility: forwarded to the root div (e.g. role="alert"). */
  role?: React.AriaRole;
  'aria-live'?: React.AriaAttributes['aria-live'];
  'aria-label'?: string;
}

const CardContainer = styled.div<{
  variant: CardVariant;
  clickable: boolean;
  pattern?: PatternType;
  patternIntensity: PatternIntensity;
  accentBorder?: 'top' | 'bottom' | 'left' | 'right' | boolean;
  hoverElevation: boolean;
}>`
  position: relative;
  background: ${props => {
    switch (props.variant) {
      case 'filled':
        return props.theme.colors.surfaceHover;
      case 'glass':
        return `rgba(255, 255, 255, 0.1)`;
      default:
        return props.theme.colors.surface;
    }
  }};

  ${props => props.variant === 'glass' && css`
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.18);
  `}

  border: ${props => {
    if (props.variant === 'glass') return 'none';
    switch (props.variant) {
      case 'outlined':
        return `1px solid ${props.theme.colors.border}`;
      default:
        return `1px solid ${props.theme.colors.border}`;
    }
  }};

  ${props => {
    if (props.accentBorder && props.accentBorder !== true) {
      const color = props.theme.colors.primary;
      const thickness = '4px';
      switch (props.accentBorder) {
        case 'top':
          return `border-top: ${thickness} solid ${color};`;
        case 'bottom':
          return `border-bottom: ${thickness} solid ${color};`;
        case 'left':
          return `border-left: ${thickness} solid ${color};`;
        case 'right':
          return `border-right: ${thickness} solid ${color};`;
      }
    } else if (props.accentBorder === true) {
      return `border-top: 4px solid ${props.theme.colors.primary};`;
    }
    return '';
  }}

  border-radius: ${borders.radius.lg};
  box-shadow: ${props => {
    switch (props.variant) {
      case 'elevated':
        return shadows.lg;
      case 'glass':
        return shadows.card;
      case 'default':
        return shadows.card;
      default:
        return 'none';
    }
  }};

  overflow: hidden;
  transition: ${animations.transition(
    ['transform', 'box-shadow', 'background'],
    animations.duration.normal,
    animations.easing.easeOut
  )};

  ${props => {
    if (props.pattern && props.pattern !== 'none') {
      const patternStyles = getPatternStyles(
        props.pattern,
        {
          background: props.theme.colors.surface,
          text: props.theme.colors.text,
        },
        props.patternIntensity
      );
      return css`
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          opacity: 0.5;
          background-image: ${patternStyles.backgroundImage};
          background-size: ${patternStyles.backgroundSize};
          background-position: ${patternStyles.backgroundPosition};
        }
      `;
    }
    return '';
  }}

  ${props =>
    props.clickable &&
    css`
    cursor: pointer;
    
    &:hover {
      transform: translateY(-4px);
      ${props.hoverElevation 
        ? `box-shadow: ${shadows.xl};`
        : `box-shadow: ${shadows.hover.lg};`
      }
    }
    
    &:active {
      transform: translateY(-2px);
    }
  `}

  > * {
    position: relative;
    z-index: 1;
  }
`;

const CardHeader = styled.div<{ variant: CardHeaderVariant }>`
  padding: ${spacing[6]} ${spacing[6]} 0 ${spacing[6]};
  margin-bottom: ${spacing[6]};
  padding-bottom: ${spacing[4]};
  position: relative;

  ${props => {
    if (props.variant === 'gradient') {
      return css`
        background: ${generateGradient(
          props.theme.colors.primary,
          props.theme.colors.secondary,
          '135deg'
        )};
        margin: 0;
        padding: ${spacing[6]};
        color: ${props.theme.colors.surface};
        
        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: ${props.theme.colors.border};
        }
      `;
    }
    if (props.variant === 'accent') {
      return css`
        border-bottom: 4px solid ${props.theme.colors.accent};
      `;
    }
    return css`
      border-bottom: 1px solid ${props.theme.colors.border};
    `;
  }}
`;

const CardContent = styled.div<{ $hasHeader: boolean }>`
  padding: ${props =>
    props.$hasHeader
      ? `0 ${spacing[6]} ${spacing[6]} ${spacing[6]}`
      : spacing[6]};
`;

const CardFooter = styled.div`
  padding: ${spacing[4]} ${spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: ${spacing[6]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-left: auto;
`;

export const Card: React.FC<CardProps> = ({
  id,
  variant = 'default',
  headerVariant = 'default',
  pattern,
  patternIntensity = 'subtle',
  accentBorder,
  hoverElevation = false,
  children,
  header,
  footer,
  actions,
  onClick,
  className,
  style,
  role,
  'aria-live': ariaLive,
  'aria-label': ariaLabel,
}) => {
  const theme = useTheme();

  return (
    <CardContainer
      id={id}
      variant={variant}
      clickable={!!onClick}
      pattern={pattern}
      patternIntensity={patternIntensity}
      accentBorder={accentBorder}
      hoverElevation={hoverElevation}
      onClick={onClick}
      className={className}
      style={style}
      role={role}
      aria-live={ariaLive}
      aria-label={ariaLabel}
    >
      {header && <CardHeader variant={headerVariant}>{header}</CardHeader>}
      <CardContent $hasHeader={!!header}>{children}</CardContent>
      {(footer || actions) && (
        <CardFooter>
          {footer}
          {actions && <CardActions>{actions}</CardActions>}
        </CardFooter>
      )}
    </CardContainer>
  );
};
